import {
  formatRelativeTime,
  parseApiDate,
} from "@/components/missions/MissionRow";
import type { Artifact, ThreadMessage, ThreadInteraction } from "@/services/api/agentic-v2";

/** One timestamp slot in the task thread (show relative, or hide as …). */
export type ThreadTimestampDisplay = {
  iso: string;
  relative: string;
  absolute: string;
  visible: boolean;
};

type ThreadItem =
  | { kind: "message"; message: ThreadMessage }
  | { kind: "step"; step: ThreadInteraction["steps"][number] };

/** Locale absolute time for hover tooltips. */
export function formatThreadAbsoluteTime(iso: string): string {
  const d = parseApiDate(iso);
  if (!d) return iso;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/** ISO timestamp used for display / collapse for one thread row. */
export function threadItemTimestampIso(item: ThreadItem): string | null {
  if (item.kind === "step") {
    return item.step.timestamp ?? null;
  }
  if (item.message.content_type === "artifact_card") {
    const artifact = (item.message.content as { artifact?: Artifact }).artifact;
    return artifact?.created_at ?? item.message.created_at ?? null;
  }
  return item.message.created_at ?? null;
}

/**
 * Collapse consecutive rows that share the same relative label (e.g. three
 * ``4m ago`` in a row → show the first, then ``…`` for the rest).
 * Rows without a timestamp break the run.
 */
export function collapseThreadTimestamps(
  isos: readonly (string | null | undefined)[]
): (ThreadTimestampDisplay | null)[] {
  const out: (ThreadTimestampDisplay | null)[] = [];
  let prevRelative: string | null = null;

  for (const raw of isos) {
    const iso = raw?.trim() ? raw : null;
    if (!iso) {
      out.push(null);
      prevRelative = null;
      continue;
    }
    const relative = formatRelativeTime(iso);
    if (!relative) {
      out.push(null);
      prevRelative = null;
      continue;
    }
    const visible = relative !== prevRelative;
    out.push({
      iso,
      relative,
      absolute: formatThreadAbsoluteTime(iso),
      visible,
    });
    prevRelative = relative;
  }
  return out;
}
